import { RowButton } from "@/components/ui/pressable";
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ClipboardEvent,
  type KeyboardEvent,
} from "react";
import { createPortal } from "react-dom";
import { useGetList } from "ra-core";
import { cn } from "@/lib/utils";
import type { OrganizationMember } from "@/components/atomic-crm/types";
import {
  findActiveMentionInEditor,
  insertTaskMemberMentionInEditor,
  renderTaskMentionEditorContent,
  serializeTaskMentionEditor,
} from "@/components/atomic-crm/tasks/taskMentionEditor";
import {
  getMemberName,
  getMemberOptionText,
} from "@/components/atomic-crm/tasks/taskMemberOptions";

/**
 * Text box where "@" picks a team member; the value keeps mentions as
 * @[Name](member:ID) tokens. Used by task descriptions and note threads.
 */
export const MemberMentionEditor = ({
  value,
  onChange,
  onBlur,
  onSubmit,
  onCancel,
  id,
  ariaLabel,
  placeholder = "Write something…",
  rows = 3,
  autoFocus = false,
  teamMemberIds,
  className,
}: {
  value?: string | null;
  onChange: (next: string) => void;
  onBlur?: () => void;
  /** Cmd/Ctrl + Enter. */
  onSubmit?: () => void;
  /** Escape (when the @ picker is closed). */
  onCancel?: () => void;
  id?: string;
  ariaLabel?: string;
  placeholder?: string;
  rows?: number;
  autoFocus?: boolean;
  /** Listed first and labelled "Project" in the picker. */
  teamMemberIds?: Array<string | number>;
  className?: string;
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionRange, setMentionRange] = useState<Range | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [mentionDropdownRect, setMentionDropdownRect] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  const teamIdSet = useMemo(
    () => new Set((teamMemberIds ?? []).map(String)),
    [teamMemberIds],
  );

  const mentionOpen = mentionRange != null;
  const searchText = mentionQuery.trim();

  const { data: members = [], isPending } = useGetList<OrganizationMember>(
    "organization_members",
    {
      filter: {
        "disabled@neq": true,
        ...(searchText ? { q: searchText } : {}),
      },
      pagination: { page: 1, perPage: 20 },
      sort: { field: "first_name", order: "ASC" },
    },
    { enabled: mentionOpen, staleTime: 10_000 },
  );

  const candidates = useMemo(() => {
    return [...members].sort((left, right) => {
      const leftOnTeam = teamIdSet.has(String(left.id)) ? 0 : 1;
      const rightOnTeam = teamIdSet.has(String(right.id)) ? 0 : 1;
      if (leftOnTeam !== rightOnTeam) return leftOnTeam - rightOnTeam;
      return getMemberName(left).localeCompare(getMemberName(right));
    });
  }, [members, teamIdSet]);

  const updateMentionDropdownRect = () => {
    const editor = editorRef.current;
    if (!editor || mentionRange == null) {
      setMentionDropdownRect(null);
      return;
    }

    const rect = editor.getBoundingClientRect();
    setMentionDropdownRect({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  };

  useLayoutEffect(() => {
    if (!mentionOpen) {
      setMentionDropdownRect(null);
      return;
    }

    updateMentionDropdownRect();

    const handleReposition = () => updateMentionDropdownRect();
    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);

    return () => {
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    };
  }, [mentionOpen, mentionQuery, candidates.length]);

  const syncEditorFromValue = (value?: string | null) => {
    const editor = editorRef.current;
    if (!editor) return;

    const nextValue = value ?? "";
    if (serializeTaskMentionEditor(editor) === nextValue) return;

    renderTaskMentionEditorContent(editor, nextValue);
  };

  useEffect(() => {
    syncEditorFromValue(value);
  }, [value]);

  useEffect(() => {
    if (!autoFocus || !editorRef.current) return;
    editorRef.current.focus();
  }, [autoFocus]);

  const updateMentionState = () => {
    const editor = editorRef.current;
    if (!editor) return;

    const activeMention = findActiveMentionInEditor(editor);
    if (!activeMention) {
      setMentionRange(null);
      setMentionQuery("");
      setHighlightedIndex(0);
      return;
    }

    setMentionRange(activeMention.range);
    setMentionQuery(activeMention.query);
    setHighlightedIndex(0);
  };

  const handleInput = () => {
    const editor = editorRef.current;
    if (!editor) return;

    const serialized = serializeTaskMentionEditor(editor);
    onChange(serialized);
    updateMentionState();
  };

  const selectCandidate = (member: OrganizationMember) => {
    if (!mentionRange) return;

    insertTaskMemberMentionInEditor(mentionRange, member);

    setMentionRange(null);
    setMentionQuery("");
    setHighlightedIndex(0);
    handleInput();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!mentionOpen || candidates.length === 0) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightedIndex((current) => (current + 1) % candidates.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedIndex(
        (current) => (current - 1 + candidates.length) % candidates.length,
      );
    } else if (event.key === "Enter") {
      event.preventDefault();
      selectCandidate(candidates[highlightedIndex]);
    } else if (event.key === "Escape") {
      event.preventDefault();
      setMentionRange(null);
      setMentionQuery("");
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLDivElement>) => {
    event.preventDefault();
    const text = event.clipboardData.getData("text/plain");
    if (!text) return;
    document.execCommand("insertText", false, text);
    handleInput();
  };

  const minHeightClass =
    rows <= 2 ? "min-h-16" : rows <= 4 ? "min-h-24" : "min-h-32";

  const mentionDropdown =
    mentionOpen && mentionDropdownRect
      ? createPortal(
          <div
            className="overflow-hidden rounded-md border bg-popover shadow-overlay"
            style={{
              position: "fixed",
              top: mentionDropdownRect.top,
              left: mentionDropdownRect.left,
              width: mentionDropdownRect.width,
              zIndex: 100,
            }}
          >
            {isPending ? (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                Loading team members…
              </div>
            ) : candidates.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                No matches for “{mentionQuery || "@"}”
              </div>
            ) : (
              <ul className="max-h-48 overflow-y-auto py-1">
                {candidates.map((member, index) => (
                  <li key={`member:${member.id}`}>
                    <RowButton
                      layout="none"
                      density="none"
                      className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-accent ${
                        index === highlightedIndex ? "bg-muted" : ""
                      }`}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => selectCandidate(member)}
                    >
                      <span>{getMemberOptionText(member)}</span>
                      <span className="ml-2 text-2xs uppercase text-muted-foreground">
                        {teamIdSet.has(String(member.id))
                          ? "Project"
                          : "Team member"}
                      </span>
                    </RowButton>
                  </li>
                ))}
              </ul>
            )}
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <div className="relative">
        <div
          ref={editorRef}
          id={id}
          role="textbox"
          aria-multiline="true"
          aria-label={ariaLabel}
          contentEditable
          suppressContentEditableWarning
          data-slot="textarea"
          className={cn(
            "border-input focus-visible:border-ring focus-visible:ring-ring aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 w-full rounded-md border bg-transparent px-3 py-2 text-sm shadow-sm transition-[color,box-shadow] outline-none focus-visible:ring-1 whitespace-pre-wrap break-words empty:before:text-muted-foreground empty:before:content-[attr(data-placeholder)]",
            minHeightClass,
            className,
          )}
          data-placeholder={placeholder}
          onInput={handleInput}
          onClick={updateMentionState}
          onKeyUp={updateMentionState}
          onKeyDown={(event) => {
            handleKeyDown(event);
            if (event.defaultPrevented) return;
            if (onCancel && event.key === "Escape") {
              event.preventDefault();
              onCancel();
              return;
            }
            // Cmd/Ctrl + Enter submits (threads).
            if (
              onSubmit &&
              event.key === "Enter" &&
              (event.metaKey || event.ctrlKey)
            ) {
              event.preventDefault();
              onSubmit();
            }
          }}
          onPaste={handlePaste}
          onBlur={onBlur}
        />
      </div>
      {mentionDropdown}
    </>
  );
};
